/**
 * Cart drawer - pure logic (no DOM access at load time).
 * Exposed as window.CartDrawerCore in the browser, module.exports in Node tests.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.CartDrawerCore = api;
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var APP_PROPERTY = 'properties[_personalize-id]';
  var ADD_PATH = /\/cart\/add(\.js)?$/;

  // Serial queue: a task starts only after the previous one settled, so a later
  // response always reflects a newer cart than an earlier one.
  function createQueue() {
    var tail = Promise.resolve();
    var pending = 0;
    var idleWaiters = [];

    function settle() {
      pending -= 1;
      if (pending === 0) {
        idleWaiters.splice(0).forEach(function (resolve) { resolve(); });
      }
    }

    function enqueue(task) {
      pending += 1;
      var run = tail.then(task);
      tail = run.then(function () {}, function () {});
      return run.then(
        function (value) { settle(); return value; },
        function (error) { settle(); throw error; }
      );
    }

    function whenIdle() {
      if (pending === 0) return Promise.resolve();
      return new Promise(function (resolve) { idleWaiters.push(resolve); });
    }

    return {
      enqueue: enqueue,
      whenIdle: whenIdle,
      get pending() { return pending; },
    };
  }

  function normalizePath(path) {
    var trimmed = String(path).replace(/\/+$/, '');
    return trimmed === '' ? '/' : trimmed;
  }

  // The personalize app hardcodes "/cart"; the theme's own route may carry a locale prefix.
  function cartPaths(rootPath) {
    var base = String(rootPath || '/').replace(/\/?$/, '/');
    var localized = normalizePath(base + 'cart');
    return localized === '/cart' ? ['/cart'] : ['/cart', localized];
  }

  function isAppAddRequest(input, init, baseHref) {
    var url = typeof input === 'string' ? input : input && input.url;
    if (!url) return false;
    var method = (init && init.method) || (input && input.method) || 'GET';
    if (String(method).toUpperCase() !== 'POST') return false;
    var pathname;
    try {
      pathname = new URL(url, baseHref).pathname;
    } catch (error) {
      return false;
    }
    if (!ADD_PATH.test(pathname)) return false;
    var body = init && init.body;
    return Boolean(body && typeof body.has === 'function' && body.has(APP_PROPERTY));
  }

  function createTicket(ttlMs) {
    var armedAt = null;
    return {
      arm: function (now) { armedAt = now; },
      isArmed: function (now) { return armedAt !== null && now - armedAt <= ttlMs; },
      consume: function () { armedAt = null; },
    };
  }

  function shouldInterceptCartNavigation(event, ticket, paths, now, origin) {
    if (!event || !ticket.isArmed(now)) return false;
    if (!event.cancelable) return false;
    if (event.userInitiated || event.hashChange || event.formData) return false;
    if (event.downloadRequest !== null && event.downloadRequest !== undefined) return false;
    if (event.navigationType !== 'push' && event.navigationType !== 'replace') return false;
    var url;
    try {
      url = new URL(event.destination.url);
    } catch (error) {
      return false;
    }
    if (url.origin !== origin) return false;
    return paths.indexOf(normalizePath(url.pathname)) !== -1;
  }

  // ok: the server confirmed. rejected: the server refused, nothing changed.
  // unknown: no usable answer, the cart may or may not have changed.
  function classifyResult(status, json) {
    if (status >= 200 && status < 300 && json) return 'ok';
    if (status >= 400 && status < 500 && json) return 'rejected';
    return 'unknown';
  }

  function parseQuantity(value) {
    if (value === null || value === undefined) return null;
    var text = String(value).trim();
    if (!/^\d+$/.test(text)) return null;
    return parseInt(text, 10);
  }

  // One request, answered or given up on: resolves { ok, status, json } once the body is read,
  // rejects after ms. The deadline covers the body too, so a stalled response cannot hold the
  // queue, the spinner or checkout forever. A timeout is an unknown outcome for the caller:
  // show the real cart, never add again.
  function timedRequest(fetchImpl, url, init, ms) {
    var controller = typeof AbortController === 'undefined' ? null : new AbortController();
    var options = {};
    Object.keys(init || {}).forEach(function (key) { options[key] = init[key]; });
    if (controller) options.signal = controller.signal;

    return new Promise(function (resolve, reject) {
      var timer = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('request timed out'));
      }, ms);
      function finish(fn, value) {
        clearTimeout(timer);
        fn(value);
      }
      fetchImpl(url, options).then(function (response) {
        return response.json().then(
          function (json) { return json; },
          function () { return null; }
        ).then(function (json) {
          finish(resolve, { ok: response.ok, status: response.status, json: json });
        });
      }).then(null, function (error) { finish(reject, error); });
    });
  }

  return {
    REQUEST_TIMEOUT_MS: 30000,
    timedRequest: timedRequest,
    createQueue: createQueue,
    cartPaths: cartPaths,
    isAppAddRequest: isAppAddRequest,
    createTicket: createTicket,
    shouldInterceptCartNavigation: shouldInterceptCartNavigation,
    classifyResult: classifyResult,
    parseQuantity: parseQuantity,
  };
});
